import type { ClientConfig } from '@sentra/contracts';
import { clientConfigSchema } from '@sentra/contracts';
import { createContext, type ReactNode, use } from 'react';

import { apiRoutes } from '@/api/routes';
import { isSupportedTimeZone } from '@/i18n/format';

/**
 * The installation's configuration, fetched once before the first render.
 *
 * **This exists because the SPA has no build-time environment variables.** Section 1.1 chose a
 * client-rendered application served same-origin, which means one image serves every installation
 * and nothing about a particular one can be baked into the bundle. Three values are needed before
 * anything can be drawn: the issuance mode, because a whole field on the busiest screen in the
 * product is present or absent according to it (AD-59); the reporting timezone, because a date
 * formatted in the browser's zone is the wrong time for the building; and the default language.
 *
 * **It is fetched with a plain `fetch`, not through the API client.** The client is built on top of
 * this configuration — it needs the language for `Accept-Language` and the timezone for the errors
 * it renders — so a provider that used it would be a cycle inside one phase. A single unauthenticated
 * GET needs none of that machinery.
 *
 * **A failure here stops the application rather than degrading it.** Defaulting the issuance mode
 * would make a whole registration screen wrong in one direction with nothing failing: an operator
 * in a pre-printed installation would be shown a form with no band field and would create records
 * with no credential all day. So `loadAppConfig` throws, and `bootstrap` renders one actionable
 * screen instead of a half-configured application.
 *
 * **Everything that branches on the issuance mode reads it from here.** Task 24.1's band field,
 * Task 25.3's pass delivery and Task 26.4's pass-stock screen all ask this module, through
 * {@link useIsPreprinted} or a sibling it grows, and a `web`-tier test scans this package for the two mode literals
 * and fails if one appears anywhere but this file — because a hard-coded default is invisible until
 * somebody in the other mode notices, months later, that the product has been quietly wrong.
 */

/** The three values, exactly as `GET /api/v1/config` answers them. */
export type AppConfig = ClientConfig;

/**
 * Where the configuration is fetched from. Relative, because the SPA is served same-origin.
 *
 * Taken from the route table rather than written again here. This module cannot use the CLIENT —
 * it runs before the session provider that builds one exists — but that argument does not extend to
 * the PATH, and a second copy of it would sit outside the drift test that checks the table against
 * the generated API document.
 */
export const CONFIG_PATH = apiRoutes.systemConfig.path;

/** Why the application could not start. The `cause` carries whatever the platform reported. */
class ConfigUnavailableError extends Error {
  constructor(reason: string, options?: { cause?: unknown }) {
    super(`the installation configuration could not be loaded: ${reason}`, options);
    this.name = 'ConfigUnavailableError';
  }
}

/**
 * Fetches and validates the configuration.
 *
 * Every failure mode is one refusal: a transport error, a non-200, a body that is not the contract,
 * and a timezone this platform's ICU does not know. The last one is checked HERE rather than at the
 * first date that is formatted, because `Intl.DateTimeFormat` throws a `RangeError` for an unknown
 * zone and that crash would surface inside whichever component happened to render a date first.
 */
export async function loadAppConfig(
  fetchImpl: typeof globalThis.fetch = globalThis.fetch,
): Promise<AppConfig> {
  let response: Response;
  try {
    response = await fetchImpl(CONFIG_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  } catch (error) {
    throw new ConfigUnavailableError('the server could not be reached', { cause: error });
  }

  if (!response.ok) {
    throw new ConfigUnavailableError(`the server answered ${String(response.status)}`);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    throw new ConfigUnavailableError('the answer was not JSON', { cause: error });
  }

  const parsed = clientConfigSchema.safeParse(body);
  if (!parsed.success) {
    throw new ConfigUnavailableError('the answer was not the documented shape');
  }
  if (!isSupportedTimeZone(parsed.data.reportingTimezone)) {
    throw new ConfigUnavailableError(
      `this browser does not know the timezone ${parsed.data.reportingTimezone}`,
    );
  }
  return parsed.data;
}

const AppConfigContext = createContext<AppConfig | null>(null);

/**
 * Publishes an already-loaded configuration to the tree.
 *
 * It takes the value rather than fetching it, which is what "before the first render" means in
 * practice: `bootstrap` awaits {@link loadAppConfig} and only then mounts anything, so no component
 * below ever has to handle a loading state for it and there is no moment where a screen is drawn
 * under a guess.
 */
export function AppConfigProvider({
  config,
  children,
}: Readonly<{
  config: AppConfig;
  children: ReactNode;
}>): ReactNode {
  return <AppConfigContext value={config}>{children}</AppConfigContext>;
}

/** The configuration. Throws outside the provider, because there is no sensible default. */
export function useAppConfig(): AppConfig {
  const config = use(AppConfigContext);
  if (config === null) {
    throw new Error('useAppConfig was called outside AppConfigProvider');
  }
  return config;
}

/**
 * Whether this installation controls meals, as a boolean.
 *
 * A named helper rather than `useAppConfig().diningEnabled` at each call site, for the reason
 * {@link useIsPreprinted} is one: the question has one answer and it should be asked one way. It is
 * read by the administration dashboard, which uses it to decide whether the dining panel is offered
 * at all.
 *
 * **The COUNTER does not ask it.** A scan's own `dining` block is `null` when the feature is off,
 * so the meal row is absent by construction rather than because a screen remembered to consult a
 * flag. That is the stronger arrangement and it is why this helper has one caller rather than
 * three.
 */
export function useDiningEnabled(): boolean {
  return useAppConfig().diningEnabled;
}

/**
 * Whether this installation issues pre-printed bands, as a boolean.
 *
 * It exists so that no screen has to write the mode's literal to ask the question. The `web`-tier
 * scan refuses `'preprinted'` and `'on_demand'` anywhere outside this file, and a helper is the
 * honest way to satisfy that rather than a comparison repeated at every branch — which is also how
 * a typo in one of them would become a screen that is silently wrong in one direction.
 */
export function useIsPreprinted(): boolean {
  return useAppConfig().passIssuanceMode === 'preprinted';
}

/**
 * Whether a screen may OFFER a way past an empty band field, as a boolean.
 *
 * True for both `defer` and `emergency`, because the CONTROL is the same either way: a checkbox
 * saying there is no band, which sends no code. What differs is what the server then does, and no
 * screen needs to know that to draw the box.
 *
 * A boolean rather than the token, for exactly the reason {@link useIsPreprinted} is one — and the
 * `web`-tier scan refuses all three of this setting's literals outside this file, so the helper is
 * the only way to ask.
 */
export function useNoBandOffered(): boolean {
  return useAppConfig().noBandAtRegistration !== 'refuse';
}

/**
 * Whether a screen may offer to MINT a pass when there is no band, which is the amendment screen's
 * question rather than the registration form's.
 *
 * `emergency` alone. The registration form asks {@link useNoBandOffered} instead, because there the
 * deferring answer offers the same control for a different outcome; the amendment screen exists to
 * bind a band, so the only declaration worth making on it is the one that produces a pass.
 */
export function useOffersEmergencyPass(): boolean {
  return useAppConfig().noBandAtRegistration === 'emergency';
}

/**
 * Whether a registration that declares no band must carry a reference code instead.
 *
 * `defer` alone. Under `emergency` a pass IS issued, so the record resolves at a door and needs no
 * second way to be found; under `refuse` there is no bandless registration to qualify. The server
 * refuses the same case with `PERSON_REFERENCE_REQUIRED`, and the form renders that refusal's own
 * sentence rather than a second one written here.
 */
export function useReferenceRequiredWithoutBand(): boolean {
  return useAppConfig().noBandAtRegistration === 'defer';
}

/** The zone every date and time in this application is rendered in. */
/**
 * Whether Section 1.7's self-service reset is offered at all.
 *
 * One boolean from the server, and the browser compares nothing: the capability is
 * `SMTP_HOST configured AND security.self_service_reset`, composed once in the api's root and read
 * here. **The sign-in screen renders its two links from this and from nothing else**, and the three
 * routes refuse on the same predicate, so a hidden control and a closed door cannot disagree.
 */
export function useSelfServiceReset(): boolean {
  return useAppConfig().selfServiceReset;
}

export function useReportingTimezone(): string {
  return useAppConfig().reportingTimezone;
}
